/* MealLogVoiceButton — inline microphone button rendered inside MealLog.
 *
 *  Doesn't duplicate MediaRecorder logic — instead dispatches a global
 *  `voice-meal-trigger` event that VoiceLogFab listens for. The FAB owns the
 *  full recording / preview / save flow. This button just exposes the
 *  entry-point in a more discoverable place (next to "Add food" actions).
 *
 *  Reflects the same recording state via a periodic poll of the FAB DOM
 *  state attribute, so users see a visual cue when recording is active.
 */
import React, { useEffect, useState } from "react";
import { Mic, Square } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import CreditBadge from "@/components/CreditBadge";  // iter1118

export default function MealLogVoiceButton() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    // Poll the FAB's recording state via its visual cue. The FAB toggles its
    // class to `animate-pulse` while recording. Cheap (200 ms tick).
    const tick = () => {
      const fab = document.querySelector('[data-testid="voice-log-fab"]');
      const recording = !!fab && fab.className.includes("animate-pulse");
      setIsRecording(recording);
    };
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, []);

  const onClick = () => {
    window.dispatchEvent(new Event("voice-meal-trigger"));
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="meal-log-voice-button"
      aria-pressed={isRecording}
      title={bg ? "Гласово добавяне на храна (1 кредит)" : "Add meal by voice (1 credit)"}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all border ${
        isRecording
          ? "bg-rose-500 text-white border-rose-500 animate-pulse"
          : isDark
            ? "bg-[hsl(223,47%,14%)] text-[#E879F9] border-[hsl(223,47%,22%)] hover:bg-[hsl(223,47%,18%)]"
            : "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100"
      }`}
    >
      {isRecording ? <Square className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
      <span>
        {isRecording
          ? (bg ? "Спри запис" : "Stop")
          : (bg ? "Гласово" : "Voice")}
      </span>
      {!isRecording && <CreditBadge featureId="voice_meal" fallbackCost={1} variant="inline" showLabel={false} className="ml-1" />}
    </button>
  );
}
